/* eslint-disable react/jsx-pascal-case */
import { useContext, useEffect, useState } from "react";
import GeneralContext from "../../context/GeneralContext/GeneralContext";
import api from "../../services/api";
import { displayMessage } from "../../helpers/messages";
import SL_Button from "../../components/buttons/Button";
import { SL_Input } from "../../components/input/Input";
import { Form, Modal, Upload } from "antd";
import { SL_Select } from "../../components/selects/Select";
import { UploadOutlined } from "@ant-design/icons";

// Opções de gênero
const GENDER_OPTIONS = [
	{ value: "male", label: displayMessage("MALE") },
	{ value: "female", label: displayMessage("FEMALE") },
];

// Opções de tipo de dieta
const TYPE_OPTIONS = [
	{ value: "weight_loss", label: displayMessage("WEIGHT_LOSS") },
	{ value: "muscle_gain", label: displayMessage("MUSCLE_GAIN") },
];

// Opções de quantidade de refeições
const MEALS_COUNT_OPTIONS = [
	{ value: 3, label: "3 refeições" },
	{ value: 4, label: "4 refeições" },
	{ value: 5, label: "5 refeições" },
	{ value: 6, label: "6 refeições" },
];

// Opções de intolerância alimentar
const INTOLERANCE_OPTIONS = [
	{ value: "none", label: "Nenhuma" },
	{ value: "lactose", label: "Lactose" },
	{ value: "gluten", label: "Glúten" },
	{ value: "vegetarian", label: "Vegetariano" },
];

export function CreateDietModal({ onCloseModal, presetCombination }) {
	const [form] = Form.useForm();
	const [fileList, setFileList] = useState([]);

	const { setShowLoading } = useContext(GeneralContext);

	// Quando aberto a partir de uma combinação (Etapa 2), pré-preenche e
	// trava os campos que definem a combinação — o admin só define sequência,
	// descrição e PDF.
	useEffect(() => {
		if (presetCombination) {
			form.setFieldsValue(presetCombination);
		}
	}, [presetCombination, form]);

	const combinationLocked = !!presetCombination;

	async function onFinishForm(values) {
		// Validação do arquivo
		if (fileList.length === 0) {
			Modal.error({
				title: displayMessage("ERROR"),
				content: displayMessage("FILE_MANDATORY"),
			});
			return;
		}

		try {
			setShowLoading(true);

			// Criação do FormData para envio multipart
			const formData = new FormData();
			formData.append("gender", values.gender);
			formData.append("type", values.type);
			formData.append("meals_count", values.meals_count);
			formData.append("food_intolerance", values.food_intolerance);
			formData.append("sequence", values.sequence ?? 1);
			if (values.description) {
				formData.append("description", values.description);
			}
			formData.append("file", fileList[0].originFileObj);

			await api.diets.create(formData);
			await onCloseModal(true);
		} catch (error) {
			console.error("Erro ao criar dieta:", error);
		} finally {
			setShowLoading(false);
		}
	}

	// Configuração do upload de arquivo
	const uploadProps = {
		beforeUpload: () => false, // Previne upload automático
		onChange: ({ fileList: newFileList }) => {
			// Permite apenas 1 arquivo
			setFileList(newFileList.slice(-1));
		},
		fileList,
		accept: ".pdf",
		maxCount: 1,
	};

	return (
		<Modal
			title={
				<div className="text-xl font-bold text-blue-700 mr-3">
					{displayMessage("ADD_DIET")}
				</div>
			}
			open={true}
			onCancel={() => onCloseModal(false)}
			centered={true}
			footer={
				<div className="flex flex-col ss:flex-row gap-2 justify-end">
					<SL_Button
						className="w-full ss:w-[150px] bg-gray-500 border-transparent"
						onClick={() => onCloseModal(false)}
					>
						{displayMessage("CLOSE")}
					</SL_Button>

					<SL_Button
						className="w-full ss:w-[150px] m-0 bg-blue-600"
						onClick={() => form.submit()}
					>
						{displayMessage("END_REGISTER")}
					</SL_Button>
				</div>
			}
			maskClosable={false}
			width={600}
		>
			<Form
				onFinish={onFinishForm}
				layout="vertical"
				form={form}
				className={`flex gap-2 flex-col`}
			>
				<div className="gap-4 flex w-full flex-col smb:flex-row">
					<Form.Item
						label={
							<span className="text-blue-700 font-semibold">
								{displayMessage("GENDER")}:
							</span>
						}
						name="gender"
						className={`w-full m-0`}
						required
						rules={[
							{
								required: true,
								message: displayMessage("GENDER_MANDATORY"),
							},
						]}
						initialValue={"male"}
					>
						<SL_Select options={GENDER_OPTIONS} disabled={combinationLocked} />
					</Form.Item>

					<Form.Item
						label={
							<span className="text-blue-700 font-semibold">
								{displayMessage("DIET_TYPE")}:
							</span>
						}
						name="type"
						className={`w-full m-0`}
						required
						rules={[
							{
								required: true,
								message: displayMessage("DIET_TYPE_MANDATORY"),
							},
						]}
						initialValue={"weight_loss"}
					>
						<SL_Select options={TYPE_OPTIONS} disabled={combinationLocked} />
					</Form.Item>
				</div>

				<Form.Item
					label={
						<span className="text-blue-700 font-semibold">
							Intolerância:
						</span>
					}
					name="food_intolerance"
					className={`w-full m-0`}
					required
					rules={[
						{
							required: true,
							message: "Intolerância é obrigatória",
						},
					]}
					initialValue={"none"}
				>
					<SL_Select options={INTOLERANCE_OPTIONS} disabled={combinationLocked} />
				</Form.Item>

			<Form.Item
				label={
					<span className="text-blue-700 font-semibold">
						{displayMessage("MEALS_COUNT")}:
					</span>
				}
				name="meals_count"
				className={`w-full m-0`}
				required
				rules={[
					{
						required: true,
						message: displayMessage("MEALS_COUNT_MANDATORY"),
					},
				]}
				initialValue={4}
			>
				<SL_Select options={MEALS_COUNT_OPTIONS} disabled={combinationLocked} />
			</Form.Item>

			<Form.Item
				label={
					<span className="text-blue-700 font-semibold">
						{displayMessage("SEQUENCE")}:
					</span>
				}
				name="sequence"
				className={`w-full m-0`}
				required
				rules={[
					{ required: true, message: displayMessage("SEQUENCE_MANDATORY") },
				]}
				initialValue={1}
				tooltip={displayMessage("SEQUENCE_HELPER")}
			>
				<SL_Input type="number" className="text-blue-700" />
			</Form.Item>

				<Form.Item
					label={
						<span className="text-blue-700 font-semibold">
							{displayMessage("DESCRIPTION")}:
						</span>
					}
					name="description"
					className={`w-full m-0`}
					initialValue={""}
				>
					<SL_Input
						textarea={true}
						rows={2}
						type="text"
						className="text-blue-700"
					/>
				</Form.Item>

				<Form.Item
					label={
						<span className="text-blue-700 font-semibold">
							{displayMessage("PDF_FILE")}:
						</span>
					}
					className={`w-full m-0`}
					required
				>
					<Upload {...uploadProps}>
						<SL_Button icon={<UploadOutlined />} type="default">
							{displayMessage("DRAG_FILE_HERE")}
						</SL_Button>
					</Upload>
				</Form.Item>
			</Form>
		</Modal>
	);
}
